import React, { useState, useEffect, useRef } from 'react';
import { utils } from '../../utils';
import {
  listarIntegracoesCliente,
  salvarIntegracaoCliente,
  deletarIntegracaoCliente,
  ClienteIntegracaoConfig,
  ConfiguracaoXml,
  ConfiguracaoEdi,
  CampoEdi
} from '../../services/etiquetas';

interface Props {
  clienteId: number;
}

const TAGS_XML_NFE = [
  { value: 'ide.nNF', label: 'Número NF-e (ide.nNF)' },
  { value: 'ide.serie', label: 'Série (ide.serie)' },
  { value: 'ide.dhEmi', label: 'Data Emissão (ide.dhEmi)' },
  { value: 'total.ICMSTot.vNF', label: 'Valor NF (total.ICMSTot.vNF)' },
  { value: 'transp.vol.qVol', label: 'Qtd Volumes (transp.vol.qVol)' },
  { value: 'transp.vol.pesoB', label: 'Peso Bruto (transp.vol.pesoB)' },
  { value: 'transp.vol.pesoL', label: 'Peso Líquido (transp.vol.pesoL)' },
  { value: 'emit.CNPJ', label: 'CNPJ Emitente (emit.CNPJ)' },
  { value: 'emit.xNome', label: 'Nome Emitente (emit.xNome)' },
  { value: 'dest.CNPJ', label: 'CNPJ Destinatário (dest.CNPJ)' },
  { value: 'dest.xNome', label: 'Nome Destinatário (dest.xNome)' },
  { value: 'prod.cEAN', label: 'Código EAN (prod.cEAN)' },
  { value: 'prod.cProd', label: 'Código SKU (prod.cProd)' },
  { value: 'prod.xProd', label: 'Descrição Produto (prod.xProd)' },
  { value: 'prod.qTrib', label: 'Quantidade Tributável (prod.qTrib)' },
  { value: 'prod.vUnTrib', label: 'Valor Unitário Tributável (prod.vUnTrib)' },
  { value: 'prod.vProd', label: 'Valor Total Produto (prod.vProd)' },
  { value: 'prod.xPed', label: 'Número Pedido (prod.xPed)' }
];

const TAGS_NUMERO_PEDIDO = [
  { value: 'xPed', label: 'xPed - Número do Pedido (padrão NF-e)', origem: 'prod' as const },
  { value: 'transp.vol.marca', label: 'transp.vol.marca - Marca/Pedido nos Volumes', origem: 'custom' as const },
  { value: 'nItemPed', label: 'nItemPed - Item do Pedido', origem: 'prod' as const },
  { value: 'cProd', label: 'cProd - Código do Produto', origem: 'prod' as const }
];

const CAMPOS_SISTEMA = [
  'codigo_barras', 'numero_pedido', 'volume_atual', 'total_volumes',
  'descricao', 'codigo_produto', 'quantidade', 'valor_unitario'
];


const IntegracaoMarketplace: React.FC<Props> = ({ clienteId }) => {
  const [integracoes, setIntegracoes] = useState<ClienteIntegracaoConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [configEditando, setConfigEditando] = useState<'xml_nfe' | 'edi' | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Configuração padrão para novas integrações XML
  const XML_CONFIG_PADRAO: ConfiguracaoXml = {
    mapeamento_tags: {},
    tag_volumes: '',
    tag_etiquetas: '',
    tag_numero_pedido: 'xPed',
    tag_origem_pedido: 'prod',
    regex_numero_pedido: ''
  };

  // XML Config State
  const [xmlConfig, setXmlConfig] = useState<ConfiguracaoXml>(XML_CONFIG_PADRAO);
  const [xmlNome, setXmlNome] = useState('Integração XML NF-e');

  // EDI Config State
  const [ediConfig, setEdiConfig] = useState<ConfiguracaoEdi>({
    codigo_linha_nfe: '310',
    codigo_linha_etiqueta: '311',
    tamanho_codigo_linha: 3,
    campos_nfe: [],
    campos_etiqueta: []
  });
  const [ediNome, setEdiNome] = useState('Integração EDI');

  // Estado para importação de XML e navegação de tags
  const [xmlTagsExtraidas, setXmlTagsExtraidas] = useState<Array<{ caminho: string; valor: string; tipo: string }>>([]);
  const [mostrarTagsBrowser, setMostrarTagsBrowser] = useState(false);
  const [filtroTags, setFiltroTags] = useState('');
  const [mostrarRegexAvancado, setMostrarRegexAvancado] = useState(false);
  const [regexTestTexto, setRegexTestTexto] = useState('');
  const [regexTestResultado, setRegexTestResultado] = useState<string | null>(null);
  const xmlFileInputRef = useRef<HTMLInputElement>(null);

  // Função para extrair todas as tags de um objeto XML recursivamente
  const extrairTagsRecursivo = (obj: any, caminho: string = '', resultado: Array<{ caminho: string; valor: string; tipo: string }> = []) => {
    if (!obj || typeof obj !== 'object') return resultado;

    for (const [chave, valor] of Object.entries(obj)) {
      if (chave === '$') continue; // pular atributos xml2js
      const caminhoAtual = caminho ? `${caminho}.${chave}` : chave;

      if (typeof valor === 'string' || typeof valor === 'number') {
        const valorStr = String(valor);
        resultado.push({
          caminho: caminhoAtual,
          valor: valorStr.length > 200 ? valorStr.substring(0, 200) + '...' : valorStr,
          tipo: caminhoAtual.includes('infCpl') || caminhoAtual.includes('infAdFisco') ? 'observacao' : 'tag'
        });
      } else if (Array.isArray(valor)) {
        // Para arrays, mostrar o primeiro item como exemplo
        if (valor.length > 0) {
          const primeiro = valor[0];
          if (typeof primeiro === 'string' || typeof primeiro === 'number') {
            resultado.push({
              caminho: `${caminhoAtual}[0]`,
              valor: `${String(primeiro)} (${valor.length} itens)`,
              tipo: 'array'
            });
          } else {
            extrairTagsRecursivo(primeiro, `${caminhoAtual}[0]`, resultado);
          }
        }
      } else if (typeof valor === 'object' && valor !== null) {
        extrairTagsRecursivo(valor, caminhoAtual, resultado);
      }
    }
    return resultado;
  };

  // Importar e parsear XML no browser
  const handleImportarXml = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const texto = await file.text();

      // Parse XML no browser usando DOMParser
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(texto, 'text/xml');

      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) {
        utils.showNotification('Arquivo XML inválido. Verifique o formato.', { type: 'error' });
        return;
      }

      // Converter XML DOM para objeto JS simples
      const xmlToObj = (node: Element): any => {
        const obj: any = {};
        // Processar filhos
        for (let i = 0; i < node.children.length; i++) {
          const child = node.children[i];
          const tagName = child.localName || child.tagName;
          const childObj = child.children.length > 0 ? xmlToObj(child) : child.textContent?.trim() || '';

          if (obj[tagName] !== undefined) {
            // Se já existe, transformar em array
            if (!Array.isArray(obj[tagName])) {
              obj[tagName] = [obj[tagName]];
            }
            obj[tagName].push(childObj);
          } else {
            obj[tagName] = childObj;
          }
        }
        // Se não tem filhos, retornar texto
        if (node.children.length === 0) {
          return node.textContent?.trim() || '';
        }
        return obj;
      };

      const rootObj = xmlToObj(xmlDoc.documentElement);
      const tags = extrairTagsRecursivo(rootObj);

      setXmlTagsExtraidas(tags);
      setMostrarTagsBrowser(true);
      utils.showNotification(`${tags.length} tags encontradas no XML!`, { type: 'success' });
    } catch (error: any) {
      utils.showNotification('Erro ao processar XML: ' + (error.message || 'erro desconhecido'), { type: 'error' });
    }

    // Reset input para permitir reimportação do mesmo arquivo
    if (xmlFileInputRef.current) xmlFileInputRef.current.value = '';
  };

  // Selecionar uma tag do browser
  const handleSelecionarTag = (tag: { caminho: string; valor: string; tipo: string }) => {
    // Limpar prefixos de raiz do XML (nfeProc.NFe.infNFe.) e índices de array
    let caminhoLimpo = tag.caminho
      .replace(/^nfeProc\./, '')
      .replace(/^NFe\./, '')
      .replace(/^infNFe\./, '')
      .replace(/\[\d+\]/g, '');

    // Extrair o nome simples da tag (última parte do caminho)
    const partes = caminhoLimpo.split('.');
    const tagSimples = partes[partes.length - 1];

    // Verificar se a tag está dentro de det.prod (tag de produto)
    const ehTagProduto = caminhoLimpo.startsWith('det.prod.') || caminhoLimpo.startsWith('det.');
    // Verificar se é campo de observação
    const ehObservacao = tag.tipo === 'observacao';

    if (ehObservacao) {
      // Para observações como infCpl, configurar como regex
      setXmlConfig(prev => ({
        ...prev,
        tag_numero_pedido: tagSimples,
        tag_origem_pedido: 'infCpl' as const
      }));
      // Sugerir abrir o regex avançado
      setMostrarRegexAvancado(true);
      setRegexTestTexto(tag.valor);
    } else if (ehTagProduto) {
      // Tags dentro de det/prod: usar apenas o nome simples (backend busca em prod/det)
      setXmlConfig(prev => ({
        ...prev,
        tag_numero_pedido: tagSimples,
        tag_origem_pedido: 'prod' as const,
        regex_numero_pedido: ''
      }));
    } else {
      // Tags em outros locais (ex: transp.vol.marca): usar caminho completo relativo a infNFe
      setXmlConfig(prev => ({
        ...prev,
        tag_numero_pedido: caminhoLimpo,
        tag_origem_pedido: 'custom' as const,
        regex_numero_pedido: ''
      }));
    }
    setMostrarTagsBrowser(false);
  };

  // Testar regex
  const handleTestarRegex = () => {
    const regex = xmlConfig.regex_numero_pedido;
    if (!regex || !regexTestTexto) {
      setRegexTestResultado(null);
      return;
    }
    try {
      const re = new RegExp(regex);
      const match = re.exec(regexTestTexto);
      if (match) {
        setRegexTestResultado(match[1] || match[0]);
      } else {
        setRegexTestResultado('(nenhum resultado encontrado)');
      }
    } catch (err: any) {
      setRegexTestResultado(`Erro na regex: ${err.message}`);
    }
  };

  useEffect(() => {
    fetchIntegracoes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  const fetchIntegracoes = async () => {
    setIsLoading(true);
    try {
      const result = await listarIntegracoesCliente(clienteId);
      if (result.success) {
        setIntegracoes(result.dados);
        // Preencher configs existentes ou usar padrão
        const xmlInt = result.dados.find(i => i.tipo_integracao === 'xml_nfe');
        if (xmlInt) {
          setXmlNome(xmlInt.nome);
          setXmlConfig(xmlInt.configuracao_xml || XML_CONFIG_PADRAO);
        } else {
          // Sem integração existente: usar valores padrão
          setXmlNome('Integração XML NF-e');
          setXmlConfig(XML_CONFIG_PADRAO);
        }
        const ediInt = result.dados.find(i => i.tipo_integracao === 'edi');
        if (ediInt) {
          setEdiNome(ediInt.nome);
          setEdiConfig(ediInt.configuracao_edi || { codigo_linha_nfe: '310', codigo_linha_etiqueta: '311', tamanho_codigo_linha: 3, campos_nfe: [], campos_etiqueta: [] });
        }
      }
    } catch (error: any) {
      console.error('Erro ao carregar integrações:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getIntegracao = (tipo: 'xml_nfe' | 'edi') => integracoes.find(i => i.tipo_integracao === tipo);

  const handleSalvarXml = async () => {
    setIsSaving(true);
    try {
      await salvarIntegracaoCliente(clienteId, {
        tipo_integracao: 'xml_nfe',
        nome: xmlNome,
        ativo: true,
        configuracao_xml: xmlConfig,
        importar_dados_nfe: true
      });
      utils.showNotification('Integração XML salva com sucesso!', { type: 'success' });
      setConfigEditando(null);
      fetchIntegracoes();
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao salvar', { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSalvarEdi = async () => {
    setIsSaving(true);
    try {
      await salvarIntegracaoCliente(clienteId, {
        tipo_integracao: 'edi',
        nome: ediNome,
        ativo: true,
        configuracao_edi: ediConfig,
        importar_dados_nfe: true
      });
      utils.showNotification('Integração EDI salva com sucesso!', { type: 'success' });
      setConfigEditando(null);
      fetchIntegracoes();
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao salvar', { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDesativar = async (id: number) => {
    if (!window.confirm('Deseja remover esta integração?')) return;
    try {
      await deletarIntegracaoCliente(id);
      utils.showNotification('Integração removida', { type: 'success' });
      fetchIntegracoes();
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao remover', { type: 'error' });
    }
  };

  const addMapeamentoTag = () => {
    setXmlConfig(prev => ({
      ...prev,
      mapeamento_tags: { ...prev.mapeamento_tags, '': '' }
    }));
  };

  const updateMapeamentoTag = (oldKey: string, newKey: string, value: string) => {
    setXmlConfig(prev => {
      const newMap = { ...prev.mapeamento_tags };
      if (oldKey !== newKey) delete newMap[oldKey];
      newMap[newKey] = value;
      return { ...prev, mapeamento_tags: newMap };
    });
  };

  const removeMapeamentoTag = (key: string) => {
    setXmlConfig(prev => {
      const newMap = { ...prev.mapeamento_tags };
      delete newMap[key];
      return { ...prev, mapeamento_tags: newMap };
    });
  };

  const addCampoEdi = (tipo: 'campos_nfe' | 'campos_etiqueta') => {
    setEdiConfig(prev => ({
      ...prev,
      [tipo]: [...(prev[tipo] || []), { nome: '', posicao_inicio: 1, posicao_fim: 10, descricao: '' }]
    }));
  };

  const updateCampoEdi = (tipo: 'campos_nfe' | 'campos_etiqueta', index: number, field: keyof CampoEdi, value: any) => {
    setEdiConfig(prev => {
      const campos = [...(prev[tipo] || [])];
      campos[index] = { ...campos[index], [field]: value };
      return { ...prev, [tipo]: campos };
    });
  };

  const removeCampoEdi = (tipo: 'campos_nfe' | 'campos_etiqueta', index: number) => {
    setEdiConfig(prev => ({
      ...prev,
      [tipo]: (prev[tipo] || []).filter((_, i) => i !== index)
    }));
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div className="loading-spinner-large"></div>
        <p>Carregando integrações...</p>
      </div>
    );
  }

  const xmlInt = getIntegracao('xml_nfe');
  const ediInt = getIntegracao('edi');

  return (
    <div>
      <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
        <i className="fas fa-store" style={{ marginRight: '8px' }}></i>
        Marketplace de Integrações
      </h3>
      <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '14px' }}>
        Configure os tipos de integração para importação automática de etiquetas deste cliente.
      </p>

      {/* Cards do Marketplace */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '24px' }}>

        {/* Card XML NF-e */}
        <div style={{
          border: xmlInt ? '2px solid #16a34a' : '2px solid #e5e7eb',
          borderRadius: '12px',
          padding: '20px',
          background: xmlInt ? '#f0fdf4' : '#fff',
          transition: 'all 0.2s'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '10px',
              background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <i className="fas fa-file-code" style={{ fontSize: '22px', color: '#2563eb' }}></i>
            </div>
            <div>
              <h4 style={{ margin: 0, fontSize: '16px' }}>XML NF-e</h4>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>Nota Fiscal Eletrônica</span>
            </div>
            {xmlInt && (
              <span style={{
                marginLeft: 'auto', background: '#16a34a', color: '#fff',
                padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600
              }}>Ativo</span>
            )}
          </div>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
            Importa etiquetas a partir de arquivos XML de NF-e. Configure quais tags do XML serão mapeadas para os campos do sistema.
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setConfigEditando('xml_nfe')}
              style={{ flex: 1 }}
            >
              <i className={`fas fa-${xmlInt ? 'cog' : 'plus'}`} style={{ marginRight: '4px' }}></i>
              {xmlInt ? 'Configurar' : 'Ativar'}
            </button>
            {xmlInt && (
              <button className="btn btn-outline btn-sm" onClick={() => handleDesativar(xmlInt.id!)}>
                <i className="fas fa-trash"></i>
              </button>
            )}
          </div>
        </div>

        {/* Card EDI */}
        <div style={{
          border: ediInt ? '2px solid #16a34a' : '2px solid #e5e7eb',
          borderRadius: '12px',
          padding: '20px',
          background: ediInt ? '#f0fdf4' : '#fff',
          transition: 'all 0.2s'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '10px',
              background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <i className="fas fa-file-alt" style={{ fontSize: '22px', color: '#d97706' }}></i>
            </div>
            <div>
              <h4 style={{ margin: 0, fontSize: '16px' }}>EDI</h4>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>Intercâmbio Eletrônico</span>
            </div>
            {ediInt && (
              <span style={{
                marginLeft: 'auto', background: '#16a34a', color: '#fff',
                padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600
              }}>Ativo</span>
            )}
          </div>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
            Importa etiquetas de arquivos EDI posicionais. Configure o código da linha e as posições de cada campo.
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setConfigEditando('edi')}
              style={{ flex: 1 }}
            >
              <i className={`fas fa-${ediInt ? 'cog' : 'plus'}`} style={{ marginRight: '4px' }}></i>
              {ediInt ? 'Configurar' : 'Ativar'}
            </button>
            {ediInt && (
              <button className="btn btn-outline btn-sm" onClick={() => handleDesativar(ediInt.id!)}>
                <i className="fas fa-trash"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Painel de Configuração XML */}
      {configEditando === 'xml_nfe' && (
        <div style={{ border: '1px solid #d1d5db', borderRadius: '10px', padding: '20px', background: '#f9fafb' }}>
          <h4 style={{ marginBottom: '16px' }}>
            <i className="fas fa-file-code" style={{ color: '#2563eb', marginRight: '8px' }}></i>
            Configuração XML NF-e
          </h4>

          <div className="form-group" style={{ marginBottom: '16px' }}>
            <label className="form-label">Nome da Integração</label>
            <input type="text" className="form-control" value={xmlNome} onChange={(e) => setXmlNome(e.target.value)} />
          </div>

          {/* Tag do Número do Pedido - Campo principal */}
          <div style={{
            marginBottom: '16px',
            padding: '16px',
            border: '2px solid #3b82f6',
            borderRadius: '10px',
            background: '#eff6ff'
          }}>
            <label className="form-label" style={{ fontWeight: 700, color: '#1d4ed8', marginBottom: '8px' }}>
              <i className="fas fa-tag" style={{ marginRight: '6px' }}></i>
              Tag do Número do Pedido
            </label>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '10px' }}>
              Selecione qual tag XML contém o número do pedido. Você pode escolher uma tag padrão,
              importar um XML para visualizar todas as tags disponíveis, ou usar Regex para extrair de campos de texto.
            </p>

            {/* Linha 1: Select + Botão Importar XML */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
              <select
                className="form-control"
                style={{ flex: 1 }}
                value={xmlConfig.tag_numero_pedido || ''}
                onChange={(e) => {
                  const tagSel = TAGS_NUMERO_PEDIDO.find(t => t.value === e.target.value);
                  setXmlConfig(prev => ({
                    ...prev,
                    tag_numero_pedido: e.target.value,
                    tag_origem_pedido: tagSel?.origem || 'prod',
                    regex_numero_pedido: ''
                  }));
                }}
              >
                <option value="">Selecione a tag...</option>
                {TAGS_NUMERO_PEDIDO.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>

              <input
                ref={xmlFileInputRef}
                type="file"
                accept=".xml"
                style={{ display: 'none' }}
                onChange={handleImportarXml}
              />
              <button
                className="btn btn-outline btn-sm"
                style={{ whiteSpace: 'nowrap', borderColor: '#3b82f6', color: '#2563eb' }}
                onClick={() => xmlFileInputRef.current?.click()}
                title="Importar um XML de exemplo para visualizar as tags disponíveis"
              >
                <i className="fas fa-file-upload" style={{ marginRight: '4px' }}></i>
                Importar XML
              </button>
              <button
                className="btn btn-outline btn-sm"
                style={{
                  whiteSpace: 'nowrap',
                  borderColor: mostrarRegexAvancado ? '#f59e0b' : '#6b7280',
                  color: mostrarRegexAvancado ? '#d97706' : '#6b7280'
                }}
                onClick={() => setMostrarRegexAvancado(!mostrarRegexAvancado)}
                title="Filtro avançado com Regex para extrair pedido de campos de texto"
              >
                <i className="fas fa-filter" style={{ marginRight: '4px' }}></i>
                Regex
              </button>
            </div>

            {/* Tag Browser - Tags extraídas do XML importado */}
            {mostrarTagsBrowser && xmlTagsExtraidas.length > 0 && (
              <div style={{
                marginTop: '12px',
                border: '1px solid #93c5fd',
                borderRadius: '8px',
                background: '#fff',
                maxHeight: '350px',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div style={{
                  padding: '10px 12px',
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: '#f0f9ff',
                  borderRadius: '8px 8px 0 0'
                }}>
                  <i className="fas fa-search" style={{ color: '#6b7280', fontSize: '13px' }}></i>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Filtrar tags... (ex: pedido, infCpl, xPed)"
                    style={{ border: 'none', background: 'transparent', boxShadow: 'none', padding: '2px 0' }}
                    value={filtroTags}
                    onChange={(e) => setFiltroTags(e.target.value)}
                  />
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ padding: '2px 8px', fontSize: '11px' }}
                    onClick={() => { setMostrarTagsBrowser(false); setFiltroTags(''); }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div style={{ overflowY: 'auto', maxHeight: '300px' }}>
                  <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                        <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Tag (caminho)</th>
                        <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Conteúdo</th>
                        <th style={{ padding: '6px 12px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', width: '80px' }}>Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {xmlTagsExtraidas
                        .filter(t => {
                          if (!filtroTags) return true;
                          const filtro = filtroTags.toLowerCase();
                          return t.caminho.toLowerCase().includes(filtro) || t.valor.toLowerCase().includes(filtro);
                        })
                        .map((tag, idx) => (
                          <tr
                            key={idx}
                            style={{
                              borderBottom: '1px solid #f3f4f6',
                              cursor: 'pointer',
                              background: tag.tipo === 'observacao' ? '#fffbeb' : undefined
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = '#eff6ff')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = tag.tipo === 'observacao' ? '#fffbeb' : '')}
                          >
                            <td style={{ padding: '6px 12px', fontFamily: 'monospace', color: '#1d4ed8', fontSize: '11px' }}>
                              {tag.tipo === 'observacao' && (
                                <i className="fas fa-sticky-note" style={{ color: '#f59e0b', marginRight: '4px', fontSize: '10px' }} title="Campo de observação - use Regex para extrair"></i>
                              )}
                              {tag.caminho}
                            </td>
                            <td style={{ padding: '6px 12px', color: '#374151', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '11px' }} title={tag.valor}>
                              {tag.valor}
                            </td>
                            <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                              <button
                                className="btn btn-primary btn-sm"
                                style={{ padding: '2px 8px', fontSize: '11px' }}
                                onClick={() => handleSelecionarTag(tag)}
                              >
                                Usar
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  {xmlTagsExtraidas.filter(t => {
                    if (!filtroTags) return true;
                    const filtro = filtroTags.toLowerCase();
                    return t.caminho.toLowerCase().includes(filtro) || t.valor.toLowerCase().includes(filtro);
                  }).length === 0 && (
                    <p style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                      Nenhuma tag encontrada com o filtro "{filtroTags}"
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Painel Regex Avançado */}
            {mostrarRegexAvancado && (
              <div style={{
                marginTop: '12px',
                padding: '14px',
                border: '1px solid #f59e0b',
                borderRadius: '8px',
                background: '#fffbeb'
              }}>
                <label className="form-label" style={{ fontWeight: 600, color: '#92400e', marginBottom: '6px', fontSize: '13px' }}>
                  <i className="fas fa-filter" style={{ marginRight: '4px' }}></i>
                  Filtro Avançado com Regex
                </label>
                <p style={{ fontSize: '12px', color: '#78716c', marginBottom: '10px' }}>
                  Use uma expressão regular para extrair o número do pedido de campos de texto (ex: observações da NF-e).
                  O primeiro grupo de captura <code>()</code> será usado como valor extraído.
                </p>

                {/* Tag de origem */}
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#92400e', marginBottom: '4px', display: 'block' }}>
                    Tag de origem do texto:
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    style={{ fontSize: '13px' }}
                    value={xmlConfig.tag_numero_pedido || ''}
                    onChange={(e) => setXmlConfig(prev => ({
                      ...prev,
                      tag_numero_pedido: e.target.value,
                      tag_origem_pedido: 'infCpl' as const
                    }))}
                    placeholder="Ex: infCpl, infAdFisco, obsCont..."
                  />
                </div>

                {/* Campo Regex */}
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#92400e', marginBottom: '4px', display: 'block' }}>
                    Expressão Regular (Regex):
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    style={{ fontFamily: 'monospace', fontSize: '13px' }}
                    value={xmlConfig.regex_numero_pedido || ''}
                    onChange={(e) => setXmlConfig(prev => ({ ...prev, regex_numero_pedido: e.target.value }))}
                    placeholder="Ex: PEDIDO:\s*(\d+)"
                  />
                </div>

                {/* Exemplos de Regex */}
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#78716c', marginBottom: '4px', display: 'block' }}>
                    Exemplos comuns (clique para usar):
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {[
                      { regex: 'PEDIDO:\\s*(\\d+)', desc: 'PEDIDO: 123456' },
                      { regex: 'PED\\.?\\s*(\\d+)', desc: 'PED 123456 ou PED. 123456' },
                      { regex: 'Espelho\\s*N\\s*(\\d+)', desc: 'Espelho N 123456' },
                      { regex: 'N(?:º|o|r)\\s*Pedido\\s*:?\\s*(\\d+)', desc: 'Nº Pedido: 123456' },
                      { regex: 'OC\\s*:?\\s*(\\d+)', desc: 'OC: 123456' },
                      { regex: 'OP\\s*:?\\s*(\\d+)', desc: 'OP: 123456' },
                    ].map((ex, i) => (
                      <button
                        key={i}
                        className="btn btn-outline btn-sm"
                        style={{ fontSize: '10px', padding: '2px 6px', borderColor: '#d97706', color: '#92400e' }}
                        onClick={() => setXmlConfig(prev => ({ ...prev, regex_numero_pedido: ex.regex }))}
                        title={`Padrão: ${ex.desc}`}
                      >
                        {ex.desc}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Área de teste do Regex */}
                <div style={{ background: '#fff', borderRadius: '6px', padding: '10px', border: '1px solid #e5e7eb' }}>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#374151', marginBottom: '4px', display: 'block' }}>
                    <i className="fas fa-flask" style={{ marginRight: '4px' }}></i>
                    Testar Regex:
                  </label>
                  <textarea
                    className="form-control"
                    style={{ fontSize: '12px', fontFamily: 'monospace', minHeight: '60px', marginBottom: '6px' }}
                    value={regexTestTexto}
                    onChange={(e) => setRegexTestTexto(e.target.value)}
                    placeholder="Cole aqui o conteúdo da tag para testar a regex (ex: conteúdo de infCpl)..."
                  />
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      className="btn btn-primary btn-sm"
                      style={{ fontSize: '12px' }}
                      onClick={handleTestarRegex}
                    >
                      <i className="fas fa-play" style={{ marginRight: '4px' }}></i>
                      Testar
                    </button>
                    {regexTestResultado !== null && (
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: regexTestResultado.startsWith('(') || regexTestResultado.startsWith('Erro') ? '#dc2626' : '#16a34a',
                        fontFamily: 'monospace'
                      }}>
                        <i className={`fas fa-${regexTestResultado.startsWith('(') || regexTestResultado.startsWith('Erro') ? 'times-circle' : 'check-circle'}`} style={{ marginRight: '4px' }}></i>
                        Resultado: {regexTestResultado}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Resumo da configuração selecionada */}
            {xmlConfig.tag_numero_pedido && (
              <div style={{ marginTop: '10px', padding: '8px 12px', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #86efac' }}>
                <p style={{ fontSize: '12px', color: '#16a34a', fontWeight: 600, margin: 0 }}>
                  <i className="fas fa-check-circle" style={{ marginRight: '4px' }}></i>
                  {xmlConfig.tag_origem_pedido === 'infCpl' && xmlConfig.regex_numero_pedido ? (
                    <>
                      O número do pedido será extraído da tag {'<'}{xmlConfig.tag_numero_pedido}{'>'} usando Regex: <code style={{ background: '#dcfce7', padding: '1px 4px', borderRadius: '3px' }}>{xmlConfig.regex_numero_pedido}</code>
                    </>
                  ) : (
                    <>
                      O número do pedido será extraído da tag: {'<'}{xmlConfig.tag_numero_pedido}{'>'}
                    </>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Mapeamento de Tags */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label className="form-label" style={{ margin: 0 }}>Mapeamento de Tags</label>
              <button className="btn btn-outline btn-sm" onClick={addMapeamentoTag}>
                <i className="fas fa-plus" style={{ marginRight: '4px' }}></i> Adicionar
              </button>
            </div>
            {Object.entries(xmlConfig.mapeamento_tags || {}).map(([campo, tag], idx) => (
              <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                <select
                  className="form-control"
                  style={{ flex: 1 }}
                  value={campo}
                  onChange={(e) => updateMapeamentoTag(campo, e.target.value, tag)}
                >
                  <option value="">Campo do sistema...</option>
                  {CAMPOS_SISTEMA.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <select
                  className="form-control"
                  style={{ flex: 1 }}
                  value={tag}
                  onChange={(e) => updateMapeamentoTag(campo, campo, e.target.value)}
                >
                  <option value="">Tag XML...</option>
                  {TAGS_XML_NFE.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <button className="btn btn-outline btn-sm" onClick={() => removeMapeamentoTag(campo)} style={{ color: '#ef4444' }}>
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            ))}
            {Object.keys(xmlConfig.mapeamento_tags || {}).length === 0 && (
              <p style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>
                Nenhum mapeamento customizado. Os campos padrão da NF-e serão extraídos automaticamente.
              </p>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button className="btn btn-outline" onClick={() => setConfigEditando(null)}>Cancelar</button>
            <button className="btn btn-primary" onClick={handleSalvarXml} disabled={isSaving}>
              {isSaving ? 'Salvando...' : 'Salvar Configuração'}
            </button>
          </div>
        </div>
      )}

      {/* Painel de Configuração EDI */}
      {configEditando === 'edi' && (
        <div style={{ border: '1px solid #d1d5db', borderRadius: '10px', padding: '20px', background: '#f9fafb' }}>
          <h4 style={{ marginBottom: '16px' }}>
            <i className="fas fa-file-alt" style={{ color: '#d97706', marginRight: '8px' }}></i>
            Configuração EDI
          </h4>

          <div className="form-group" style={{ marginBottom: '16px' }}>
            <label className="form-label">Nome da Integração</label>
            <input type="text" className="form-control" value={ediNome} onChange={(e) => setEdiNome(e.target.value)} />
          </div>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '16px' }}>
            <div className="form-group">
              <label className="form-label">Código Linha NF-e</label>
              <input type="text" className="form-control" value={ediConfig.codigo_linha_nfe || ''} onChange={(e) => setEdiConfig(prev => ({ ...prev, codigo_linha_nfe: e.target.value }))} placeholder="Ex: 310" />
            </div>
            <div className="form-group">
              <label className="form-label">Código Linha Etiqueta</label>
              <input type="text" className="form-control" value={ediConfig.codigo_linha_etiqueta || ''} onChange={(e) => setEdiConfig(prev => ({ ...prev, codigo_linha_etiqueta: e.target.value }))} placeholder="Ex: 311" />
            </div>
            <div className="form-group">
              <label className="form-label">Tamanho Código Linha</label>
              <input type="number" className="form-control" value={ediConfig.tamanho_codigo_linha || 3} onChange={(e) => setEdiConfig(prev => ({ ...prev, tamanho_codigo_linha: parseInt(e.target.value) || 3 }))} min={1} max={10} />
            </div>
          </div>

          {/* Campos NF-e */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label className="form-label" style={{ margin: 0 }}>Campos da Linha NF-e</label>
              <button className="btn btn-outline btn-sm" onClick={() => addCampoEdi('campos_nfe')}>
                <i className="fas fa-plus" style={{ marginRight: '4px' }}></i> Adicionar
              </button>
            </div>
            {(ediConfig.campos_nfe || []).map((campo, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                <input type="text" className="form-control" placeholder="Nome campo" style={{ flex: 2 }} value={campo.nome} onChange={(e) => updateCampoEdi('campos_nfe', idx, 'nome', e.target.value)} />
                <input type="number" className="form-control" placeholder="Início" style={{ width: '80px' }} value={campo.posicao_inicio} onChange={(e) => updateCampoEdi('campos_nfe', idx, 'posicao_inicio', parseInt(e.target.value) || 0)} min={1} />
                <input type="number" className="form-control" placeholder="Fim" style={{ width: '80px' }} value={campo.posicao_fim} onChange={(e) => updateCampoEdi('campos_nfe', idx, 'posicao_fim', parseInt(e.target.value) || 0)} min={1} />
                <button className="btn btn-outline btn-sm" onClick={() => removeCampoEdi('campos_nfe', idx)} style={{ color: '#ef4444' }}>
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            ))}
            {(ediConfig.campos_nfe || []).length === 0 && (
              <p style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>Nenhum campo configurado para a linha de NF-e.</p>
            )}
          </div>

          {/* Campos Etiqueta */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label className="form-label" style={{ margin: 0 }}>Campos da Linha Etiqueta</label>
              <button className="btn btn-outline btn-sm" onClick={() => addCampoEdi('campos_etiqueta')}>
                <i className="fas fa-plus" style={{ marginRight: '4px' }}></i> Adicionar
              </button>
            </div>
            {(ediConfig.campos_etiqueta || []).map((campo, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                <input type="text" className="form-control" placeholder="Nome campo" style={{ flex: 2 }} value={campo.nome} onChange={(e) => updateCampoEdi('campos_etiqueta', idx, 'nome', e.target.value)} />
                <input type="number" className="form-control" placeholder="Início" style={{ width: '80px' }} value={campo.posicao_inicio} onChange={(e) => updateCampoEdi('campos_etiqueta', idx, 'posicao_inicio', parseInt(e.target.value) || 0)} min={1} />
                <input type="number" className="form-control" placeholder="Fim" style={{ width: '80px' }} value={campo.posicao_fim} onChange={(e) => updateCampoEdi('campos_etiqueta', idx, 'posicao_fim', parseInt(e.target.value) || 0)} min={1} />
                <button className="btn btn-outline btn-sm" onClick={() => removeCampoEdi('campos_etiqueta', idx)} style={{ color: '#ef4444' }}>
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            ))}
            {(ediConfig.campos_etiqueta || []).length === 0 && (
              <p style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>Nenhum campo configurado para a linha de etiqueta.</p>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button className="btn btn-outline" onClick={() => setConfigEditando(null)}>Cancelar</button>
            <button className="btn btn-primary" onClick={handleSalvarEdi} disabled={isSaving}>
              {isSaving ? 'Salvando...' : 'Salvar Configuração'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegracaoMarketplace;
